import React from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { UserMinus, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function OffboardCarriers() {
  const { data: carriers = [], isLoading } = useList("offboarded-carriers", "/carriers/offboarded");
  const invalidate = useInvalidate();

  const restore = async (c) => {
    if (!window.confirm(`Restore ${c.carrier_name} to Active Carriers?`)) return;
    try {
      await api.post(`/carriers/${c.id}/restore`);
      toast.success(`${c.carrier_name} restored to Active Carriers`);
      invalidate(["offboarded-carriers", "carriers"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="OffBoard Carriers" subtitle="Carriers that are no longer taking service from us, with the reason and date of offboarding" />
      <Panel className="p-0">
        {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : carriers.length === 0 ? (
          <EmptyState icon={UserMinus} title="No Offboarded Carriers" hint="Use the Offboard button on Active Carriers to move a carrier here." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead className="hidden md:table-cell">MC / USDOT</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Offboard Date</TableHead>
                  <TableHead className="hidden lg:table-cell">Offboarded By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carriers.map((c, i) => (
                  <TableRow key={c.id} className="border-slate-100" data-testid={`offboarded-row-${c.id}`}>
                    <TableCell className="text-slate-400 font-mono text-sm">{i + 1}</TableCell>
                    <TableCell><p className="font-semibold text-slate-900">{c.carrier_name}</p><p className="text-xs text-slate-500">{c.email || c.phone || "—"}</p></TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs text-slate-600">{c.mc_number || "—"} / {c.usdot || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700 max-w-xs" data-testid={`offboarded-reason-${c.id}`}>{c.offboard_reason || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700 whitespace-nowrap" data-testid={`offboarded-date-${c.id}`}>{fmtDate(c.offboard_date)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600">{c.offboarded_by_name || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" onClick={() => restore(c)} className="h-8 border-slate-300 bg-white text-slate-700 text-xs" data-testid={`restore-carrier-${c.id}`}>
                        <ArrowCounterClockwise size={14} className="mr-1" />Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}
